// import ExitToAppIcon from '@mui/icons-material/ExitToApp';
// import { Button } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from "@mui/system";
import { useEffect, useMemo, useState } from "react";
import { appManager, AppManager, EventType } from "./AppManager";
import { WarningToast } from "./components/WarningToast/WarningToast";
import { UserDetails } from "./components/UserDetails/UserDetails";
import { darkTheme } from "./styles/AppTheme";
import { LocationData } from "./types/location";
import { InfoToast } from "./components/InfoToast/InfoToast";
import { Survey } from "./components/Survey/Survey";
import { HeaderComponent } from "./components/Header/HeaderComponent";
import HelpInteraction from "./components/HelpInteraction/HelpInteraction";
import TimeLapse from "./components/Timelapse/TimeLapse";
import CheckFaultsFlow from "./components/CheckFaults/CheckFaultsFlow";
import OverlayLoader from "./components/Common/OverlayLoader";
import { Footer } from "./components/Footer/Footer";
import { SixMonthsLater } from "./components/Timelapse/SixMonthsLater";
import { IncompatibleDevice } from "./components/Incompatible/IncompatibleDevice";
import { IncompatibleBrowser } from "./components/Incompatible/IncompatibleBrowser";

const App = (props: { appManager: AppManager }) => {
  const locationData: Array<LocationData> = [
    { name: "Nurse's Station", value: "Nurse's Station" },
    { name: "Server Room", value: "Server Room" },
    { name: "Imaging Room", value: "Imaging Room" },
    { name: "Office Room", value: "Office Room" },
  ];

  const [flow, setFlow] = useState<EventType | undefined>();
  const [showHeader, setShowHeader] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState("Entering room...");
  const [locationInfo, setLocationInfo] = useState("Nurse's Station");
  const [showProgressLoader, setShowProgressLoader] = useState(true);

  useEffect(() => {
    appManager.registerAppMessageCallback((event: EventType, data: any) => {
      console.log(event, "event app.tsx");
      switch (event) {
        case EventType.Intro:
          setShowHeader(false);
          setShowFooter(false);
          break;
        case EventType.ShowWarningAndInfo:
          setShowInfo(true);
          setShowWarning(true);
          break;
        case EventType.HideInfo:
          setShowInfo(false);
          break;
        case EventType.HideWarning:
          setShowWarning(false);
          break;
        case EventType.ShowLoader:
          setShowLoader(true);
          setLoaderMessage(data);
          break;
        case EventType.HideLoader:
          setShowLoader(false);
          break;
        case EventType.HideProgressLoader:
          setShowProgressLoader(false);
          break;
        case EventType.ShowUserGuide:
          setShowFooter(false);
          break;
        case EventType.Survey:
          setShowFooter(false);
          break;
        case EventType.SetLocationInfo:
          setLocationInfo(data);
          break;
        case EventType.IncompatibleDevice:
          setShowHeader(false);
          break;
        case EventType.IncompatibleBrowser:
          setShowHeader(false);
          break;
        default:
          setShowHeader(true);
          setShowFooter(true);
      }
      setFlow(event);
    });
  }, []);

  useEffect(() => {
    compatibilityCheck();
  }, []);

  const compatibilityCheck = () => {
    appManager.sendMessageToHost(EventType.CompatibilityCheck, "CompatibilityCheck");
  };

  const exit = () => {
    setFlow(undefined);
    appManager.sendMessageToHost(EventType.Exit, "Exit Flow");
  };

  const hideIntro = () => {
    setFlow(EventType.ShowUserGuide);
  };

  const getLocation = (currentLocation: string) => {
    appManager.sendMessageToHost(EventType.CurrentLocation, {
      currentLocation,
    });
  };

  const experienceStart = () => {
    appManager.sendMessageToHost(EventType.ExperienceStart, "ExperienceStart");
  };

  const scenario3Stage1Finish = () => {
    appManager.sendMessageToHost(
      EventType.Scenario3Stage1Finish,
      "Scenario3Stage1Finish",
    );
  };

  const scenario3Stage2Finish = () => {
    appManager.sendMessageToHost(
      EventType.Scenario3Stage2Finish,
      "Scenario3Stage2Finish",
    );
  };

  const scenario4Finish = () => {
    appManager.sendMessageToHost(EventType.Scenario4Finish, "Scenario4Finish");
  };

  const MRIisFixed = () => {
    appManager.sendMessageToHost(EventType.MRIisFixed, "MRIisFixed");
  };

  const scenario5Stage1Finish = () => {
    appManager.sendMessageToHost(
      EventType.Scenario5Stage1Finish,
      "Scenario3Stage1Finish",
    );
  };

  const scenario5Stage2Finish = () => {
    appManager.sendMessageToHost(
      EventType.Scenario5Stage2Finish,
      "Scenario5Stage2Finish",
    );
  };

  const replay = () => {
    appManager.sendMessageToHost(EventType.Replay, "Replay");
  };

  const audio = (state: boolean) => {
    appManager.sendMessageToHost(EventType.Audio, state);
  };

  const childComponent = useMemo(() => {
    switch (flow) {
      case EventType.ShowWarning:
        return <WarningToast open={true} />;
      case EventType.ShowInfoWithButton:
        return <InfoToast open={true} showButton={true} />;
      case EventType.ShowInfoWithoutButton:
        return <InfoToast open={true} showButton={false} />;
      case EventType.Intro:
        return <UserDetails hideIntro={hideIntro} showProgress={showProgressLoader} />;
      case EventType.HideProgressLoader:
        return <UserDetails hideIntro={hideIntro} showProgress={showProgressLoader} />;
      case EventType.ShowUserGuide:
        return (
          <HeaderComponent
            enableCoachMark={true}
            locationData={locationData}
            getLocation={getLocation}
            locationInfo={locationInfo}
            exit={exit}
            experienceStart={experienceStart}
            audio={audio}
          />
        );
      case EventType.Scenario3Stage1:
        return (
          <HelpInteraction
            scenario3Stage1Finish={scenario3Stage1Finish}
            scenario3Stage2Finish={scenario3Stage2Finish}
          />
        );
      case EventType.Scenario3Stage2:
        return (
          <HelpInteraction
            scenario3Stage1Finish={scenario3Stage1Finish}
            scenario3Stage2Finish={scenario3Stage2Finish}
            startSlide="delete_malicious_code"
          />
        );
      case EventType.Scenario5Stage1:
        return <SixMonthsLater scenario5Stage1Finish={scenario5Stage1Finish} />;
      case EventType.Scenario5Stage2:
        return <TimeLapse scenario5Stage2Finish={scenario5Stage2Finish} />;
      case EventType.Survey:
        return <Survey replay={replay} />;
      case EventType.Scenario4:
        return (
          <CheckFaultsFlow
            MRIisFixed={MRIisFixed}
            scenario4Finish={scenario4Finish}
          />
        );
      case EventType.ShowLoader:
        return <OverlayLoader message={loaderMessage} />;
      case EventType.IncompatibleDevice:
        return <IncompatibleDevice />;
      case EventType.IncompatibleBrowser:
        return <IncompatibleBrowser />;
    }
  }, [flow]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={darkTheme}>
        <>
          {showHeader ? (
            <HeaderComponent
              enableCoachMark={false}
              locationData={locationData}
              getLocation={getLocation}
              locationInfo={locationInfo}
              exit={exit}
              experienceStart={experienceStart}
              audio={audio}
            />
          ) : null}
          {showInfo ? <InfoToast open={true} showButton={false} /> : null}
          {showWarning ? <WarningToast open={true} /> : null}
          {showLoader ? <OverlayLoader message={loaderMessage} /> : null}
          {childComponent}
          {showFooter ? <Footer /> : null}
          {/* {!flow && <Button onClick={() => { setFlow(undefined); appManager.sendMessageToHost(EventType.Exit, 'Case 2') }}
            sx={{ borderRadius: '48px', textTransform: 'capitalize', padding: '20px 40px' }} variant="contained" endIcon={<ExitToAppIcon />}>Exit</Button>
          } */}
        </>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
